import { useEffect, useState, useCallback } from 'react';
import { fetchMe, fetchConversations, createConversation, renameConversation, cloneConversation } from './api';
import type { User, Conversation } from './api';
import { Login } from './components/Login';
import { Sidebar } from './components/Sidebar';
import { Chat } from './components/Chat';
import './App.css';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    fetchMe().then((me) => {
      setUser(me);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchConversations().then(setConversations).catch(console.error);
  }, [user]);

  const handleCreate = useCallback(async () => {
    const conversation = await createConversation('Nueva conversación');
    setConversations((prev) => [conversation, ...prev]);
    setActiveId(conversation.id);
    setMobileSidebarOpen(false);
  }, []);

  const handleTicketCreated = useCallback(
    (conversationId: string, key: string, url: string, title: string) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, status: 'created' as const, ticketKey: key, ticketUrl: url, title }
            : c
        )
      );
    },
    []
  );

  const handleRename = useCallback(async (conversationId: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, title } : c))
    );
    try {
      await renameConversation(conversationId, title);
    } catch (err) {
      console.error('Rename error:', err);
    }
  }, []);

  const handleTicketUpdated = useCallback((conversationId: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, updatedAt: new Date().toISOString() } : c))
    );
  }, []);

  const handleClone = useCallback(async (conversationId: string) => {
    try {
      const cloned = await cloneConversation(conversationId);
      setConversations((prev) => [cloned, ...prev]);
      setActiveId(cloned.id);
      setMobileSidebarOpen(false);
    } catch (err) {
      console.error('Clone error:', err);
    }
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    setConversations([]);
    setActiveId(null);
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <span>Cargando BK Jira Agent...</span>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  const activeConversation = conversations.find((c) => c.id === activeId) || null;

  return (
    <div className="app-layout">
      {!activeConversation && (
        <button
          className="mobile-menu-button"
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Abrir conversaciones"
        >
          ☰
        </button>
      )}
      <Sidebar
        user={user}
        conversations={conversations}
        activeId={activeId}
        onSelect={setActiveId}
        onCreate={handleCreate}
        onRename={handleRename}
        onClone={handleClone}
        onLogout={handleLogout}
        mobileOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
      />
      <div className="main-content">
        {activeConversation ? (
          <Chat
            conversation={activeConversation}
            user={user}
            onBack={() => setActiveId(null)}
            onTicketCreated={handleTicketCreated}
            onTicketUpdated={handleTicketUpdated}
          />
        ) : (
          <div className="empty-state">
            <span className="empty-state-icon">💬</span>
            <h2>Bienvenido, {user.displayName}</h2>
            <p>Selecciona una conversación existente o crea una nueva para empezar a trabajar con el agente.</p>
            <button className="create-button" onClick={handleCreate}>
              <span>+</span>
              <span>Nueva conversación</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
